'use client'

/**
 * MatchCarousel — mostra i pannelli partita in uno slider orizzontale
 * con scroll-snap e dot indicator.
 *
 * Visibile a qualsiasi numero di partite: se ≤ visibleCount le card
 * riempiono lo spazio senza scroll; se > visibleCount compare lo slider.
 */

import { useRef, useState, useEffect, useCallback } from 'react'
import { MatchState, TeamId, TEAMS } from '@/types/match'
import ScorePanel from './ScorePanel'

interface MatchItem {
  teamId: string
  state:  MatchState
}

interface Props {
  matches:      MatchItem[]
  /** Quante card sono visibili contemporaneamente (default 1) */
  visibleCount?: number
  className?:   string
}

export default function MatchCarousel({ matches, visibleCount = 1, className = '' }: Props) {
  // Suddivide i match in pagine da `visibleCount`
  const pages: MatchItem[][] = []
  for (let i = 0; i < matches.length; i += visibleCount) {
    pages.push(matches.slice(i, i + visibleCount))
  }

  const trackRef = useRef<HTMLDivElement>(null)
  const [active, setActive] = useState(0)

  const onScroll = useCallback(() => {
    const el = trackRef.current
    if (!el) return
    setActive(Math.round(el.scrollLeft / el.clientWidth))
  }, [])

  useEffect(() => {
    const el = trackRef.current
    if (!el) return
    el.addEventListener('scroll', onScroll, { passive: true })
    return () => el.removeEventListener('scroll', onScroll)
  }, [onScroll])

  function scrollTo(i: number) {
    trackRef.current?.scrollTo({ left: trackRef.current.clientWidth * i, behavior: 'smooth' })
  }

  const showDots = pages.length > 1

  return (
    <div className={`flex flex-col gap-2 ${className}`}>
      {/* ── Track: ogni pagina è una colonna di `visibleCount` card ──────── */}
      <div
        ref={trackRef}
        className="flex overflow-x-auto"
        style={{ scrollSnapType: 'x mandatory', scrollbarWidth: 'none' }}
      >
        {pages.map((page, pi) => (
          <div
            key={pi}
            className="flex flex-col gap-3 w-full flex-shrink-0"
            style={{ scrollSnapAlign: 'start' }}
          >
            {page.map(({ teamId, state }) => {
              const team = TEAMS[teamId as TeamId]
              return (
                <ScorePanel
                  key={teamId}
                  state={state}
                  teamId={team ? teamId as TeamId : undefined}
                  accentColor={team?.color ?? '#FE7200'}
                  variant="card"
                />
              )
            })}
          </div>
        ))}
      </div>

      {/* ── Dot indicator ─────────────────────────────────────────────────── */}
      {showDots && (
        <div className="flex justify-center items-center gap-1.5 pt-1">
          {pages.map((_, i) => (
            <button
              key={i}
              type="button"
              onClick={() => scrollTo(i)}
              aria-label={`Pagina ${i + 1}`}
              className="transition-all duration-200"
              style={{
                width:      active === i ? 16 : 6,
                height:     6,
                borderRadius: 3,
                background: active === i ? '#FE7200' : 'rgba(255,255,255,0.2)',
              }}
            />
          ))}
        </div>
      )}
    </div>
  )
}
